<template>
  <div v-if="!isLoading && data.length" class="date-swiper">
    <button class="date-swiper__prev-button" :class="{hidden: isBeginning}" @click="prevSlide">
      <svg
        height="20"
        width="20"
        style="transform: rotate(180deg);"
      >
        <use xlink:href="#next" />
      </svg>
    </button>
    <button class="date-swiper__next-button" :class="{hidden: isEnd}" @click="nextSlide">
      <svg
        height="20"
        width="20"
      >
        <use xlink:href="#next" />
      </svg>
    </button>
    <swiper
      v-once
      ref="swiperRef"
      :navigation="false"
      :mousewheel="false"
      :slides-per-view="'auto'"
      :space-between="12"
      @swiper="onSwiper"
      @slide-change="onSlideChange"
      @reach-beginning="(swiper) => isBeginning = true"
      @reach-end="(swiper) => isEnd = true"
    >
      <swiper-slide
        v-for="date in dates"
        :key="date"
        :class="{
          'last-of-month': date.isLastOfMonth
        }"
        @click="chooseDate(date)"
      >
        <div
          v-if="date.isFirstOfMonth && currentMonth !== date.month"
          class="date-swiper__month"
        >
          {{ date.month }}
        </div>
        <div
          class="date-swiper__day-container"
          :class="{'date-swiper__day-container_active': isActiveDate(date)}"
        >
          <div
            class="date-swiper__date"
          >
            {{ date.date.format('DD') }}
          </div>
          <div
            :class="{'date-swiper__day_weekend': date.isWeekend}"
            class="date-swiper__day"
          >
            {{ date.date.format('dd').toUpperCase() }}
          </div>
        </div>
      </swiper-slide>
      <swiper-slide class="date-swiper__more-dates more-dates" style="flex:1">
        <IDatePicker
          v-model="dateRange"
          :min-date="nextMonthStart"
          :start-date="nextMonthStart"
          :active-dates="data"
          @on-date-select="chooseDateMore"
        >
          <template #trigger>
            <div class="more-dates__btn">
              Другая дата
              <svg height="24" width="24">
                <use xlink:href="#calendar2" />
              </svg>
            </div>
          </template>
        </IDatePicker>
      </swiper-slide>
    </swiper>
    <div class="date-swiper__current-month">{{ currentMonth }}</div>
  </div>
  <IDateSwiperSkeleton v-else-if="isLoading" />
</template>

<script setup>
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/swiper-bundle.min.css';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import utc from 'dayjs/plugin/utc';
import {
  ref, watch, toRefs, computed,
} from 'vue';
import useCalendarStore from '@vueApp/store/modules/calendar.js';
import IDatePicker from '@vueApp/IDatePicker/IDatePicker.vue';
import IDateSwiperSkeleton from './IDateSwiperSkeleton.vue';

dayjs.extend(utc);
dayjs.locale('ru');

const props = defineProps({
  modelValue: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue']);

const { getActualDates, calendarState } = useCalendarStore();
const { data, isLoading } = toRefs(calendarState);

const swiperRef = ref(null);
const dates = ref([]);
const isBeginning = ref(true);
const isEnd = ref(false);
const currentMonth = ref('');
const nextMonthStart = ref(new Date());

const dateRange = computed({
  get: () => props.modelValue.map((date) => (date ? dayjs(date.date).toDate() : null)),
  set: (newValue) => emit('update:modelValue', newValue.map((date) => dayjs(date).toISOString())),
});

const calculateDates = (datesList) => {
  let previousMonth = null;
  return datesList.map((timestamp, index, arr) => {
    const currentDate = dayjs.utc(timestamp * 1000);
    const nextDate = arr[index + 1] ? dayjs(arr[index + 1] * 1000) : null;

    const currMonth = currentDate.format('MM');
    const nextMonth = nextDate?.format('MM') || null;

    const isLastOfMonth = currMonth !== nextMonth;
    const isFirstOfMonth = previousMonth !== currMonth;
    previousMonth = currMonth;

    return {
      date: currentDate,
      isLastOfMonth,
      isFirstOfMonth,
      isWeekend: [0, 6].includes(currentDate.day()),
      month: currentDate.format('MMMM'),
    };
  });
};

const chooseDate = (date) => {
  let selectedDates = [...props.modelValue];

  if (selectedDates[0] === date) {
    selectedDates = [];
    emit('update:modelValue', selectedDates);
    return;
  }

  if (selectedDates.length === 0 || selectedDates.length === 2) {
    selectedDates = [date];
  } else {
    selectedDates.push(date);
  }

  emit('update:modelValue', selectedDates);
};

function chooseDateMore(datesMore) {
  emit('update:modelValue', datesMore);
}

const nextSlide = () => swiperRef.value?.slideNext();
const prevSlide = () => swiperRef.value?.slidePrev();

const onSwiper = (instance) => {
  swiperRef.value = instance;
  isBeginning.value = instance.isBeginning;
  isEnd.value = instance.isEnd;
};

const onSlideChange = (instance) => {
  isBeginning.value = instance.isBeginning;
  isEnd.value = instance.isEnd;

  const { activeIndex } = instance;
  const activeDate = dates.value[activeIndex];

  if (activeDate) {
    currentMonth.value = activeDate.month;
  }
};

const isActiveDate = (date) => props.modelValue.find((item) => item.date.isSame(date.date));

watch(data, (value) => {
  dates.value = calculateDates(value);
});

getActualDates();
</script>

<style scoped lang="scss">
.date-swiper {
  width: 100%;
  position: relative;
  user-select: none;

  &__day-container {
    width: calc(100% - 4px);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    align-self: flex-start;
    gap: 12px;
    padding: 8px 0px;
    margin: 2px;
    cursor: pointer;

    &_active {
      outline: 2px solid $primary;
      border-radius: 12px;
    }
  }

  &__date {
    font-size: 24px;
    font-weight: 700;
    color: $deep-gray-blue;
  }

  &__month {
    font-size: 16px;
    color: $grey-text;
  }

  &__current-month {
    position: absolute;
    left: 3px;
    top: 1px;
    color: $grey-text;
  }

  &__day {
    font-size: 16px;
    color: $grey-text;

    &_weekend {
      color: $error;
    }
  }

  &__prev-button, &__next-button {
    position: absolute;
    top: 42px;
    padding: 10px;
    cursor: pointer;
    background: none;
    border: none;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__prev-button {
    left: -25px;
    z-index: 1;

    &:not(.hidden) ~ .swiper-container {
      margin-left: 36px;
    }
  }

  &__next-button {
    right: -25px;
    z-index: 1;

    &:not(.hidden) ~ .swiper-container {
      margin-right: 36px;
    }
  }

  &__more-dates.more-dates {
    align-self: center;
    margin-left: auto;
    min-width: max-content;
    max-width: max-content;
    white-space: nowrap;
    margin-right: 0;
  }

  @media screen and (max-width: $screen-md-min) {
    &__prev-button {
      left: 0;
      padding: 10px 0;
    }

    &__next-button {
      right: 0;
      padding: 10px 0;
    }

    &__current-month,
    &__month {
      text-transform: capitalize;
    }

    &__date {
      font-size: 16px;
    }

    &__day {
      font-size: 14px;
    }
  }
}

.more-dates {
  &__btn {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 8px 16px;
    border: 1px solid $black;
    border-radius: 20px;
    gap: 16px;
    font-size: 16px;
    line-height: 16px;
    cursor: pointer;
  }
}

.hidden {
  display: none;
}

.swiper-slide {
  min-width: 60px;
  max-width: 60px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  align-self: flex-end;
  gap: 12px;

  &.last-of-month {
    min-width: 140px;
    max-width: 140px;
    padding-right: 80px;
  }

  @media screen and (max-width: $screen-md-min) {
    min-width: 45px;
    max-width: 45px;

    &.last-of-month {
      .date-swiper__day-container {
        max-width: 45px;
      }
    }
  }
}

.swiper-slide-active {
  .date-swiper__month {
    align-self: flex-start;
  }
}
  </style>
